import {
  Plus, Search, Ban, CheckCircle2, Pencil, KeyRound, Unlock, MonitorSmartphone,
  Clock, Calendar, Trash2, Shield, Settings2, MoreVertical, LogOut,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PLAN_BADGE, fmtDate, daysUntil } from "@/lib/supervisorFormat";
import AgentStatusCell from "./AgentStatusCell";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

/**
 * Multi-tenant list — search + status filter + one row per tenant with
 * ~10 icon actions on the right. This is the busiest tab in the panel.
 *
 * Props all come from the parent ``SupervisorDashboard`` so the tab
 * remains stateless / testable.
 */
export default function TenantsTab({
  tenants, filtered, filter, setFilter, statusFilter, setStatusFilter,
  onCreate, onSuspend, onDelete, onResetPin, onEdit, onExtendSub, onClearLockouts,
  onForceLogout, onNewDeviceCode, onResetGrace, onToggleDeviceTrust,
}) {
  const navigate = useNavigate();
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-zinc-400 text-sm">
          <span className="font-bold text-zinc-200">{filtered.length}</span> από {tenants.length} πελάτες
        </div>
        <button
          onClick={onCreate}
          data-testid="sup-create-tenant"
          className="h-10 px-4 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-bold text-sm flex items-center gap-2 shadow-lg shadow-emerald-900/30"
        >
          <Plus size={16} /> Νέος Πελάτης
        </button>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Αναζήτηση κωδικό, όνομα, ΑΦΜ, τηλέφωνο..."
            data-testid="sup-tenant-search"
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm outline-none focus:border-amber-500"
          />
        </div>
        <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-lg p-1">
          {[
            { id: "all", label: "Όλοι" },
            { id: "active", label: "Ενεργοί" },
            { id: "suspended", label: "Σε αναστολή" },
            { id: "trial", label: "Trial" },
            { id: "agent_online", label: "Agent Online" },
            { id: "agent_offline", label: "Agent Offline" },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setStatusFilter(opt.id)}
              data-testid={`sup-filter-${opt.id}`}
              className={`h-8 px-3 rounded text-xs font-bold transition-all ${
                statusFilter === opt.id ? "bg-amber-600 text-white" : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-zinc-400 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">Κωδικός / Επιχείρηση</th>
              <th className="px-4 py-3 text-left">Plan</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Local Agent</th>
              <th className="px-4 py-3 text-right">Μέγεθος</th>
              <th className="px-4 py-3 text-right">Πελάτες / Προϊόντα</th>
              <th className="px-4 py-3 text-left">Trial / Συνδρομή</th>
              <th className="px-4 py-3 text-left">Τελ. Σύνδεση</th>
              <th className="px-4 py-3 text-right">Ενέργειες</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-zinc-500">
                  Δεν υπάρχουν πελάτες
                </td>
              </tr>
            )}
            {filtered.map((t) => {
              const trialLeft = daysUntil(t.trial_end);
              const subLeft = daysUntil(t.subscription_end);
              return (
                <tr key={t.tenant_code} className="border-t border-zinc-800 hover:bg-zinc-900/70" data-testid={`sup-tenant-row-${t.tenant_code}`}>
                  <td className="px-4 py-3">
                    <div className="font-mono font-bold text-amber-400">{t.tenant_code}</div>
                    <div className="text-zinc-300">{t.business_name}</div>
                    {t.business_afm && <div className="text-xs text-zinc-500">ΑΦΜ {t.business_afm}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${PLAN_BADGE[t.plan] || "bg-slate-700 text-slate-200"}`}>
                      {t.plan}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {t.suspended ? (
                      <span className="text-rose-400 text-xs font-bold flex items-center gap-1">
                        <Ban size={12} /> ΑΝΑΣΤΟΛΗ
                      </span>
                    ) : (
                      <span className="text-emerald-400 text-xs font-bold flex items-center gap-1">
                        <CheckCircle2 size={12} /> ΕΝΕΡΓΟΣ
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`sup-agent-status-${t.tenant_code}`}>
                    <AgentStatusCell tenant={t} />
                  </td>
                  <td className="px-4 py-3 text-right text-zinc-400 font-mono text-xs">
                    {(t.db_size_mb ?? 0).toFixed(2)} MB
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-zinc-400">
                    {t.counts?.users ?? 0} χρήστες · {t.counts?.products ?? 0} προϊόντα<br />
                    {t.counts?.tables ?? 0} τραπέζια · {t.counts?.receipt_logs ?? 0} αποδείξεις
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {t.subscription_end ? (
                      <span className={subLeft < 0 ? "text-rose-400" : subLeft < 30 ? "text-amber-400" : "text-emerald-400"}>
                        Sub: {fmtDate(t.subscription_end)}<br />
                        <span className="font-bold">{subLeft < 0 ? "Έληξε" : `${subLeft} ημ.`}</span>
                      </span>
                    ) : t.trial_end ? (
                      <span className={trialLeft < 0 ? "text-rose-400" : trialLeft < 5 ? "text-amber-400" : "text-zinc-400"}>
                        Trial: {fmtDate(t.trial_end)}<br />
                        <span className="font-bold">{trialLeft < 0 ? "Έληξε" : `${trialLeft} ημ.`}</span>
                      </span>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{fmtDate(t.last_login_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {/* Primary quick action — kept as a big prominent button */}
                      <button
                        onClick={() => navigate(`/supervisor/tenants/${t.tenant_code}/manage`)}
                        data-testid={`sup-manage-${t.tenant_code}`}
                        title="Διαχείριση Πελάτη (Modules, Backup, Πίνακες, Υγεία δεδομένων)"
                        className="h-9 px-3 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold flex items-center gap-1.5 shadow shadow-blue-900/30"
                      >
                        <Settings2 size={14} /> Διαχείριση
                      </button>

                      {/* All secondary actions live inside an actions dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          data-testid={`sup-actions-${t.tenant_code}`}
                          className="h-9 w-9 grid place-items-center rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700"
                          title="Ενέργειες"
                        >
                          <MoreVertical size={16} />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-64 bg-zinc-900 border-zinc-800 text-zinc-100"
                        >
                          <DropdownMenuLabel className="text-zinc-400 text-[10px] uppercase tracking-wider">
                            Πελάτης {t.tenant_code}
                          </DropdownMenuLabel>
                          <DropdownMenuItem
                            onClick={() => onEdit(t)}
                            data-testid={`sup-edit-${t.tenant_code}`}
                            className="cursor-pointer focus:bg-zinc-800"
                          >
                            <Pencil size={14} className="mr-2" /> Επεξεργασία στοιχείων
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onResetPin(t)}
                            data-testid={`sup-reset-pin-${t.tenant_code}`}
                            className="cursor-pointer focus:bg-zinc-800"
                          >
                            <KeyRound size={14} className="mr-2" /> Reset PIN Super Manager
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onExtendSub(t)}
                            data-testid={`sup-extend-${t.tenant_code}`}
                            className="cursor-pointer focus:bg-zinc-800"
                          >
                            <Calendar size={14} className="mr-2" /> Παράταση Συνδρομής
                          </DropdownMenuItem>

                          <DropdownMenuSeparator className="bg-zinc-800" />
                          <DropdownMenuLabel className="text-zinc-400 text-[10px] uppercase tracking-wider">
                            Ασφάλεια & Συσκευές
                          </DropdownMenuLabel>
                          <DropdownMenuItem
                            onClick={() => onClearLockouts(t)}
                            data-testid={`sup-clear-lockouts-${t.tenant_code}`}
                            className="cursor-pointer focus:bg-zinc-800 text-emerald-400"
                          >
                            <Unlock size={14} className="mr-2" /> Καθαρισμός Lockouts
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onForceLogout(t)}
                            data-testid={`sup-force-logout-${t.tenant_code}`}
                            className="cursor-pointer focus:bg-zinc-800 text-emerald-400"
                          >
                            <LogOut size={14} className="mr-2" /> Force logout χειριστών
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onNewDeviceCode(t)}
                            data-testid={`sup-new-device-${t.tenant_code}`}
                            className="cursor-pointer focus:bg-zinc-800"
                          >
                            <MonitorSmartphone size={14} className="mr-2" /> Κωδικός νέας συσκευής POS
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onToggleDeviceTrust(t)}
                            data-testid={`sup-toggle-device-trust-${t.tenant_code}`}
                            className="cursor-pointer focus:bg-zinc-800"
                          >
                            <Shield size={14} className="mr-2" /> On/Off ελέγχου συσκευής
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onResetGrace(t)}
                            data-testid={`sup-reset-grace-${t.tenant_code}`}
                            className="cursor-pointer focus:bg-zinc-800 text-amber-400"
                          >
                            <Clock size={14} className="mr-2" /> Reset 24h grace period
                          </DropdownMenuItem>

                          <DropdownMenuSeparator className="bg-zinc-800" />
                          <DropdownMenuLabel className="text-zinc-400 text-[10px] uppercase tracking-wider">
                            Κατάσταση Λογαριασμού
                          </DropdownMenuLabel>
                          {t.suspended ? (
                            <DropdownMenuItem
                              onClick={() => onSuspend(t, false)}
                              data-testid={`sup-unsuspend-${t.tenant_code}`}
                              className="cursor-pointer focus:bg-zinc-800 text-emerald-400"
                            >
                              <CheckCircle2 size={14} className="mr-2" /> Επανενεργοποίηση
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => onSuspend(t, true)}
                              data-testid={`sup-suspend-${t.tenant_code}`}
                              className="cursor-pointer focus:bg-zinc-800 text-amber-400"
                            >
                              <Ban size={14} className="mr-2" /> Αναστολή
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => onDelete(t)}
                            data-testid={`sup-delete-${t.tenant_code}`}
                            className="cursor-pointer focus:bg-rose-950 text-rose-400"
                          >
                            <Trash2 size={14} className="mr-2" /> ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
